import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import { IconBook, IconPlus } from "@tabler/icons-react";
import { Button, Group, Loader, Text, Title } from "@mantine/core";
import "./OrganizationContent.css";

function AdminTabTrainingCoursesPage() {
  const navigate = useNavigate();
  const { user, getVerifiedJWT } = useAuth();

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [courseStatusFilter, setCourseStatusFilter] = useState("all");

  const canManageCourses =
    user && (user.role === "MANAGER" || user.role === "COMPANY_ADMIN");

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const jwt = await getVerifiedJWT();
      const courseScope =
        user?.role === "COMPANY_ADMIN" ? "company" : "manager";
      const response = await fetch(
        getApiEndpoint(`/v1/training/courses/${courseScope}`),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch training courses");
      }

      const result = await response.json();
      setCourses(result.data || []);
    } catch (error) {
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canManageCourses) return;
    fetchCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const getCourseDisplayStatus = course => {
    if (course.is_active === false) return "DISABLED";
    const hasVideos = course.videos && course.videos.length > 0;
    return hasVideos ? "ACTIVE" : "DRAFT";
  };

  const filteredCourses = courses.filter(course => {
    if (courseStatusFilter === "all") return true;
    const status = getCourseDisplayStatus(course);
    if (courseStatusFilter === "active") return status === "ACTIVE";
    if (courseStatusFilter === "draft") return status === "DRAFT";
    if (courseStatusFilter === "disabled") return status === "DISABLED";
    return true;
  });

  const activeCount = courses.filter(
    course => getCourseDisplayStatus(course) === "ACTIVE"
  ).length;
  const draftCount = courses.filter(
    course => getCourseDisplayStatus(course) === "DRAFT"
  ).length;
  const disabledCount = courses.filter(
    course => getCourseDisplayStatus(course) === "DISABLED"
  ).length;

  const handleCourseClick = course => {
    const courseId = course.course_id || course.id;
    if (courseId) navigate(`/training-courses/edit/${courseId}`);
  };

  if (loading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="lg" />
        <Text>Loading training courses...</Text>
      </Group>
    );
  }

  return (
    <div className="org-section-content">
      <div className="carousel-section">
        <Group justify="space-between" mb="md">
          <div>
            <Title order={3}>Training Courses</Title>
            <Text c="dimmed" size="sm">
              {courses.length} {courses.length === 1 ? "course" : "courses"}
            </Text>
          </div>
          {canManageCourses && (
            <Button
              leftSection={<IconPlus size={14} />}
              onClick={() => navigate("/training-courses/new")}
              variant="default"
              size="md"
              radius="md"
              style={{
                backgroundColor: "white",
                border: "1px solid #dee2e6",
                color: "#2d3748",
                fontWeight: 500,
              }}
            >
              New Course
            </Button>
          )}
        </Group>

        <Group gap="xs" mb="xl">
          {[
            { key: "all", label: "All" },
            { key: "active", label: `Active (${activeCount})` },
            { key: "draft", label: `Drafts (${draftCount})` },
            { key: "disabled", label: `Inactive (${disabledCount})` },
          ].map(({ key, label }) => (
            <Button
              key={key}
              size="sm"
              variant="default"
              radius="md"
              onClick={() => setCourseStatusFilter(key)}
              style={{
                backgroundColor:
                  courseStatusFilter === key ? "#f1f3f5" : "white",
                border: "1px solid #dee2e6",
                color: "#2d3748",
                fontWeight: courseStatusFilter === key ? 600 : 400,
              }}
            >
              {label}
            </Button>
          ))}
        </Group>

        {filteredCourses.length === 0 ? (
          <Text c="dimmed" size="sm" ta="center" py="xl" fs="italic">
            No courses in this category
          </Text>
        ) : (
          <div className="course-cards-grid">
            {filteredCourses.map(course => (
              <div
                key={course.id}
                className="course-card"
                onClick={() => handleCourseClick(course)}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleCourseClick(course);
                  }
                }}
              >
                <div className="course-card__thumbnail">
                  {course.course_thumbnail ? (
                    <img
                      src={course.course_thumbnail}
                      alt={course.course_title}
                      className="course-card__image"
                      loading="lazy"
                    />
                  ) : (
                    <div className="course-card__placeholder">
                      <IconBook size={44} color="rgba(255,255,255,0.85)" />
                    </div>
                  )}
                  <div className="course-card__gradient-overlay" />
                </div>

                <div className="course-card__content">
                  <h3 className="course-card__title">{course.course_title}</h3>
                  {course.course_desc && (
                    <p className="course-card__desc">{course.course_desc}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminTabTrainingCoursesPage;
